import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useLegal } from '../lib/legalStrings';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Sparkles, ArrowLeft, Check, CreditCard, Loader2, Flame, Shield, Crown, X, Info
} from 'lucide-react';
import { toast } from 'sonner';

const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';

export default function BillingPage() {
  const { t, language } = useLanguage();
  const L = useLegal(language);
  const { user, usage, isAdmin, refreshUsage } = useAuth();
  const navigate = useNavigate();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  // Consent gate state (per EU consumer law)
  const [consentPlan, setConsentPlan] = useState(null); // plan object shown in modal
  const [waiverChecked, setWaiverChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  useEffect(() => {
    loadPlans();
    refreshUsage();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await subscriptionsAPI.getPlans();
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const openConsentGate = (plan) => {
    setConsentPlan(plan);
    setWaiverChecked(false);
    setTermsChecked(false);
  };

  const closeConsentGate = () => {
    if (processingPlan) return;
    setConsentPlan(null);
  };

  const handleSubscribe = async () => {
    if (!consentPlan || !waiverChecked || !termsChecked) return;
    const planId = consentPlan.plan_id;
    setProcessingPlan(planId);
    
    try {
      const originUrl = window.location.origin;
      const response = await subscriptionsAPI.createCheckout(planId, originUrl, {
        withdrawal_waived: true,
        terms_accepted: true
      });
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create checkout session';
      toast.error(message);
      setProcessingPlan(null);
    }
  };

  const getPlanDisplayName = (plan) => {
    switch (plan) {
      case 'admin': return 'Admin';
      case 'free_trial': return 'Free Trial';
      case 'starter': return 'Starter';
      case 'pro': return 'Pro';
      case 'agency': return 'Agency';
      default: return 'Free Trial';
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'admin': return 'bg-gradient-to-r from-amber-700 to-orange-700 text-white';
      case 'pro': return 'bg-primary text-primary-foreground';
      case 'agency': return 'bg-gradient-to-r from-primary to-accent text-white';
      case 'starter': return 'bg-blue-500 text-white';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const renderCurrentPlanInfo = () => {
    if (isAdmin) {
      return (
        <div className="dashboard-card bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="font-semibold">Admin Account</p>
                <p className="text-sm text-muted-foreground">
                  Unlimited generations • Full access to all features
                </p>
              </div>
            </div>
            <Badge className="bg-gradient-to-r from-amber-700 to-orange-700 text-white">
              <Crown className="w-3 h-3 mr-1" />
              Admin
            </Badge>
          </div>
        </div>
      );
    }

    const currentPlan = usage?.subscription_plan || 'free_trial';
    
    if (currentPlan === 'free_trial') {
      const freeRemaining = usage?.free_trial_remaining || 0;
      const freeLimit = usage?.generations_limit || 20;
      const usedGenerations = freeLimit - freeRemaining;
      const percentUsed = freeLimit > 0 ? (usedGenerations / freeLimit) * 100 : 0;
      
      return (
        <div className="dashboard-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Flame className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="font-semibold">Free Trial{BETA_MODE ? ' — Beta' : ''}</p>
                <p className="text-sm text-muted-foreground">
                  {freeRemaining} of {freeLimit} free generations remaining
                </p>
              </div>
            </div>
            <Badge variant="secondary">Free Trial</Badge>
          </div>
          <Progress value={100 - percentUsed} className="h-2" />
          {freeRemaining === 0 && !BETA_MODE && (
            <p className="text-sm text-destructive mt-2">
              You've used all your free generations. Upgrade to continue!
            </p>
          )}
          {freeRemaining === 0 && BETA_MODE && (
            <p className="text-sm text-muted-foreground mt-2">
              You've used all your beta generations. The paid plans launch after the beta ends.
            </p>
          )}
        </div>
      );
    }

    // Paid plan
    const isUnlimited = usage?.generations_limit >= 999999;
    const percentUsed = isUnlimited ? 0 : (usage?.generations_used / usage?.generations_limit) * 100;
    
    return (
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold capitalize">{currentPlan} Plan</p>
              <p className="text-sm text-muted-foreground">
                {isUnlimited 
                  ? `${usage?.generations_used || 0} generations used this month`
                  : `${usage?.generations_used || 0} of ${usage?.generations_limit || 0} generations used`
                }
              </p>
            </div>
          </div>
          <Badge className={getPlanBadgeColor(currentPlan)}>
            {getPlanDisplayName(currentPlan)}
          </Badge>
        </div>
        {!isUnlimited && <Progress value={percentUsed} className="h-2" />}
        <p className="text-xs text-muted-foreground mt-2">
          {usage?.generations_remaining || 0} generations remaining • Resets monthly
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="billing-page">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-heading text-xl font-bold">{t('dashboard.billing')}</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Current Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="font-heading text-lg font-semibold mb-4">Current Plan</h2>
          {renderCurrentPlanInfo()}
        </motion.div>

        {/* Plans - Hide for Admin */}
        {!isAdmin && BETA_MODE && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-primary/40 bg-primary/5 p-10 text-center overflow-hidden relative"
            data-testid="beta-notice"
          >
            <div className="absolute inset-0 bg-hero-glow opacity-10 pointer-events-none" />
            <div className="relative">
              <Badge className="mb-4 bg-primary text-primary-foreground border-0" data-testid="beta-badge">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                BETA
              </Badge>
              <h2 className="font-heading text-3xl font-bold mb-3">
                {language === 'bg' ? 'AIgnyx е в 10-дневна безплатна бета' :
                 language === 'es' ? 'AIgnyx está en beta gratuita de 10 días' :
                 language === 'de' ? 'AIgnyx ist in einer 10-tägigen kostenlosen Beta' :
                 'AIgnyx is in a 10-day free beta'}
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                {language === 'bg' ? 'Наслаждавайте се на удвоен безплатен лимит от 40 генерации, докато финализираме платените планове. Не се изисква карта.' :
                 language === 'es' ? 'Disfruta del doble de generaciones gratuitas (40) mientras finalizamos los planes de pago. No se requiere tarjeta.' :
                 language === 'de' ? 'Genieße die verdoppelte kostenlose Grenze von 40 Generationen, während wir die bezahlten Pläne fertigstellen. Keine Karte erforderlich.' :
                 'Enjoy a doubled free tier of 40 generations while we finalize the paid plans. No card required.'}
              </p>
              <div className="inline-flex items-center gap-2 rounded-full bg-background/60 border border-border px-4 py-2 text-sm text-muted-foreground">
                <CreditCard className="w-4 h-4" />
                {language === 'bg' ? 'Платените планове идват скоро' :
                 language === 'es' ? 'Planes de pago próximamente' :
                 language === 'de' ? 'Bezahlte Pläne demnächst verfügbar' :
                 'Paid plans coming soon'}
              </div>
            </div>
          </motion.div>
        )}

        {!isAdmin && !BETA_MODE && (
          <div>
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">{t('pricing.title')}</h2>
              <p className="text-muted-foreground">{t('pricing.subtitle')}</p>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="dashboard-card animate-pulse h-96" />
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {plans.map((plan, index) => (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative bg-card rounded-3xl p-8 border ${
                      plan.plan_id === 'pro' ? 'border-primary shadow-lg shadow-primary/10' : 'border-border'
                    }`}
                    data-testid={`plan-${plan.plan_id}`}
                  >
                    {plan.plan_id === 'pro' && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                        Most Popular
                      </div>
                    )}
                    
                    <div className="text-center mb-8">
                      <h3 className="font-heading text-xl font-semibold mb-2">{plan.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="font-heading text-4xl font-bold">${plan.price}</span>
                        <span className="text-muted-foreground">/month</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {plan.ads_per_month >= 999999 ? 'Unlimited' : plan.ads_per_month} generations/month
                      </p>
                    </div>
                    
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      className={`w-full rounded-full ${plan.plan_id === 'pro' ? '' : ''}`}
                      variant={plan.plan_id === 'pro' ? 'default' : 'outline'}
                      onClick={() => openConsentGate(plan)}
                      disabled={processingPlan === plan.plan_id || usage?.subscription_plan === plan.plan_id}
                      data-testid={`subscribe-${plan.plan_id}`}
                    >
                      {processingPlan === plan.plan_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : usage?.subscription_plan === plan.plan_id ? (
                        'Current Plan'
                      ) : (
                        t('pricing.cta')
                      )}
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin Notice */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-8 text-muted-foreground"
          >
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>As an admin, you have unlimited access to all features.</p>
            <p className="text-sm">No subscription required.</p>
          </motion.div>
        )}
      </main>

      {/* EU consumer law consent gate — required at purchase time */}
      <AnimatePresence>
        {consentPlan && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={closeConsentGate}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              className="relative bg-card border border-border rounded-3xl p-6 max-w-lg w-full shadow-2xl"
              data-testid="checkout-consent-modal"
            >
              <button
                onClick={closeConsentGate}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                aria-label="Close"
                disabled={!!processingPlan}
              >
                <X className="w-4 h-4" />
              </button>

              <h2 className="font-heading text-xl font-bold mb-1">{L.checkout.title}</h2>
              <p className="text-xs text-muted-foreground mb-5">
                {L.checkout.selectedPlanLabel}: <span className="font-medium text-foreground">{consentPlan.name} · ${consentPlan.price}/mo</span>
              </p>

              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 mb-5">
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold mb-1">{L.checkout.waiverTitle}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{L.checkout.waiverBody}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={waiverChecked}
                    onCheckedChange={(v) => setWaiverChecked(!!v)}
                    id="waiver-check"
                    data-testid="waiver-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-xs leading-relaxed">
                    {L.checkout.waiverCheckbox}
                    <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={termsChecked}
                    onCheckedChange={(v) => setTermsChecked(!!v)}
                    id="terms-check"
                    data-testid="terms-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-xs leading-relaxed">
                    {L.checkout.termsAcceptLabel}
                    <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                    {' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-primary underline underline-offset-2"
                    >
                      {L.checkout.readTerms}
                    </Link>
                  </span>
                </label>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={closeConsentGate}
                  disabled={!!processingPlan}
                  data-testid="consent-cancel"
                >
                  {L.checkout.cancelBtn}
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSubscribe}
                  disabled={!waiverChecked || !termsChecked || !!processingPlan}
                  data-testid="consent-continue"
                >
                  {processingPlan ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    L.checkout.continueBtn
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
